import { useBeforeUnload, useBlocker } from "@remix-run/react";
import { useCallback, useEffect } from "react";

// AIDEV-NOTE: EVO-2477. In-meditation capture saves only on an explicit Save — nothing is
// autosaved and nothing is restored on remount (PartNoteCapture deliberately starts a NEW note
// each time), so text typed and then navigated away from is simply gone. Gina lost a note this
// way during the HWA pass.
//
// Two exits, two mechanisms, because neither covers the other: `useBlocker` catches in-app
// route changes (the capture panel lives inside a resource page, so a sidebar link is the
// common one) and `useBeforeUnload` catches tab close, reload and back out of the app. Closing
// the player overlay is NOT a navigation and is not covered here — the overlay's own button
// saves first, which is why it reads "Save & Close".
//
// window.confirm rather than a styled dialog to match the one already in
// client-case-notes-form.tsx. A blocked navigation has to resolve synchronously in this effect
// — proceed() or reset() — or the router stays wedged, and a Radix dialog cannot promise that.

const DEFAULT_MESSAGE =
    "Your note hasn't been saved yet. Leave without saving it?";

/**
 * Warn before an unsaved in-meditation capture is abandoned.
 *
 * @param isDirty Whether the viewer has typed something not yet persisted. Callers compare
 *   against what they last SAVED, not against empty — re-editing after a save is dirty again.
 */
export function useUnsavedCaptureWarning(
    isDirty: boolean,
    message: string = DEFAULT_MESSAGE,
) {
    const blocker = useBlocker(
        ({ currentLocation, nextLocation }) =>
            isDirty && currentLocation.pathname !== nextLocation.pathname,
    );

    useEffect(() => {
        if (blocker.state !== "blocked") return;
        if (window.confirm(message)) {
            blocker.proceed();
        } else {
            blocker.reset();
        }
    }, [blocker, message]);

    useBeforeUnload(
        useCallback(
            (event: BeforeUnloadEvent) => {
                if (!isDirty) return;
                // AIDEV-NOTE: Browsers show their own wording here and ignore ours; both lines
                // are still required for the prompt to appear at all across engines.
                event.preventDefault();
                event.returnValue = "";
            },
            [isDirty],
        ),
        { capture: true },
    );
}

// AIDEV-NOTE: useBlocker throws outside a DATA router. Every real mount is under root.tsx's, but a
// bare `render()` in a unit test is not — so a test that renders any meditation surface containing
// the capture rail must stub THIS module (see fullscreen-streaming-player.unit.test.tsx). Stub the
// hook, not @remix-run/react, or the component loses the rest of the router with it.
